import { DrawerForm, DrawerSizeEnum, Switch, SwitchColorEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DetectionModal } from '@/components/DetectionModal';
import { InputFiles } from '@/components/FormFields/InputFiles';
import { InputLabels } from '@/components/FormFields/InputLabels';
import { InputLogos } from '@/components/FormFields/InputLogos';
import { InputSwitch } from '@/components/FormFields/InputSwitch';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import ReferedImages from '@/components/ReferedImages/ReferedImages';
import { MEGABYTES_TO_BYTES } from '@/constants';
import { DrawerFormDataType, FormTypeEnum, State } from '@/types';
import { IMAGE_PNG_JPG_ACCEPT } from '@/utils/files';

import { useLogosDrawerFormState } from '../../hooks/useLogosDrawerFormState';
import { TLogosForm } from '../../types';

export type TLogosDrawerFormProps = {
  isOpen: boolean;
  data?: DrawerFormDataType<TLogosForm>;
  onClose: () => void;
  onSubmit: (data: TLogosForm) => void;
  isMutating: boolean;
};

export const LogosDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: TLogosDrawerFormProps) => {
  const { t } = useTranslation();
  const { form, initialValues } = useLogosDrawerFormState();

  const handleSubmit = form.handleSubmit(data => {
    const { include_in_clip_summary, ...restData } = data;
    onSubmit({
      ...restData,
      parameters: {
        ...restData.parameters,
        include_in_clip_summary: include_in_clip_summary,
      },
    });
  });

  const resetForm = () => {
    form.reset(initialValues);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!data?.data) return;

    const formData: TLogosForm = {
      ...data.data,
      description: data.data.description ?? '',
      main_logo: data.data.parameters?.main_logo_presigned_url ?? '',
      logos: [],
      logos_presigned_urls: data.data.parameters?.logos_presigned_urls ?? [],
      alias: data.data.alias ?? '',
      state: data.data.state,
      include_in_clip_summary: data.data.parameters?.include_in_clip_summary ?? false,
      labels: data.data.labels ?? [],
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }

    if (data.type === FormTypeEnum.DUPLICATE) {
      form.reset({
        ...formData,
        name: `${data.data.name} (Copia)`,
      });
    }
  }, [data]);

  const { active_detection, include_in_clip_summary } = useWatch({
    control: form.control,
  });
  const isUpdating = data?.type === FormTypeEnum.UPDATE;
  const [openDetectionModal, setOpenDetectionModal] = useState(false);

  return (
    <>
      <DrawerForm
        title={t(
          data?.type === FormTypeEnum.CREATE || data?.type === FormTypeEnum.DUPLICATE
            ? 'Crear nuevo logotipo'
            : 'Editar logotipo'
        )}
        isOpen={isOpen}
        onCancel={() => {
          onClose();
          resetForm();
        }}
        isMutating={isMutating}
        onSubmit={handleSubmit}
        size={DrawerSizeEnum.MD}
        labels={{
          submit: t('Guardar'),
          cancel: t('Cancelar'),
        }}
      >
        <InputSwitch
          states={{ active: State.Active, inactive: State.Inactive }}
          form={form}
          name="state"
        />
        <RowContainer>
          <InputText
            form={form}
            name="name"
            required
            label={t('Nombre de la marca')}
            placeholder={t('Escribe aqui el nombre')}
          />
          <InputText
            form={form}
            name="alias"
            label={t('Alias')}
            placeholder={t('Escribe aquí los alias separados por comas')}
          />
        </RowContainer>
        <InputText
          form={form}
          name="description"
          label={t('Descripción')}
          placeholder={t('Escribe aqui la descripción')}
        />

        <InputLabels form={form} name="labels" label={t('Etiquetas')} />

        <div className="flex items-end gap-4">
          <InputFiles
            form={form}
            name="main_logo"
            label={t('Logo principal')}
            required={active_detection}
            isImage
            singleFile
            accept={IMAGE_PNG_JPG_ACCEPT}
            placeholder="Sube una imagen"
          />

          <div className="flex w-full flex-col gap-2">
            <Switch
              className="flex w-full"
              endLabel={t('Detección por imagen')}
              checked={active_detection}
              color={SwitchColorEnum.SUCCESS}
              onChange={active => {
                if (active) {
                  return form.setValue('active_detection', true);
                }

                if (isUpdating) {
                  return setOpenDetectionModal(true);
                }

                form.setValue('active_detection', false);
              }}
            />
            <Switch
              className="flex w-full"
              endLabel={t('Incluir en el resumen de cortes')}
              checked={include_in_clip_summary ?? false}
              color={SwitchColorEnum.SUCCESS}
              onChange={active => {
                form.setValue('include_in_clip_summary', active);
              }}
            />
          </div>
        </div>
        <div
          className={clsx('w-full', {
            'pointer-events-none opacity-50': !active_detection,
          })}
        >
          <ReferedImages />

          <InputLogos
            form={form}
            name="logos"
            newFiles
            label={t('Seleccionar archivos')}
            format={t('imagen')}
            accept={IMAGE_PNG_JPG_ACCEPT}
            maxSize={25 * MEGABYTES_TO_BYTES}
          />
          <InputLogos
            form={form}
            name="logos_presigned_urls"
            label={t('Imágenes subidas')}
            format={t('imagen')}
            maxSize={25 * MEGABYTES_TO_BYTES}
          />
        </div>
      </DrawerForm>
      <DetectionModal
        onClose={() => setOpenDetectionModal(false)}
        form={form}
        open={openDetectionModal}
      />
    </>
  );
};
